// Offensive / forbidden username detection (Vietnamese + English).
// Pure helpers (no DB) -> easy to test.
//
// NOTE: copy of server/app/utils/usernameFilter.js, used for instant
// feedback on /register. The server check is authoritative — keep both in sync.

// Common leetspeak substitutions.
const LEET = { "0": "o", "1": "i", "!": "i", "3": "e", "4": "a", "@": "a", "5": "s", "$": "s", "7": "t", "8": "b" };

// Lowercase, strip Vietnamese accents (đ -> d), undo leetspeak.
const normalize = (v) =>
	String(v || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/đ/gi, "d")
		.toLowerCase()
		.replace(/[0134!@5$78]/g, (c) => LEET[c]);

// Collapse repeated letters: "fuuuck" -> "fuck".
const squeeze = (s) => s.replace(/(.)\1+/g, "$1");

// Rejected anywhere in the name once separators are removed
// ("john_fuck", "f.u.c.k", "ditmeban"). Only words that don't occur inside
// ordinary names/words belong here.
const SUBSTRINGS = [
	// English
	"fuck", "fuk", "fck", "shit", "bitch", "cunt", "whore", "slut", "pussy", "penis",
	"vagina", "asshole", "bastard", "motherf", "nigger", "nigga", "faggot", "retard",
	"porn", "rapist", "paedo", "pedophil", "incest", "molest", "hentai", "blowjob",
	"handjob", "cumshot", "dildo", "orgasm", "boobs", "jerkoff", "wanker", "horny",
	"xxx", "sexy", "nazi", "hitler", "killyourself",
	// Vietnamese (accent-free)
	"ditme", "duma", "ditcon", "ditbo", "ditma", "ditnhau", "dumame", "dume", "dumma", "dummay",
	"dcm", "dmm", "vcl", "vkl", "clgt", "cailon", "conlon", "concac", "concak", "caidit",
	"daubuoi", "cutcho", "occho", "occac", "nunglon", "nungloz", "thangcho", "conchode",
	"chode", "lonme", "buoime", "cacme", "hiepdam", "thudam", "khieudam", "aidam",
	"mebay", "deome", "deomay", "phave", "dicho", "gaigoi",
	// Impersonation of staff / system accounts
	"admin", "moderator", "official",
];

// Short/ambiguous words: rejected only as a whole word of the name (split on
// separators and digits), so "editor", "longnguyen", "grape", "torpedo",
// "analyst" stay allowed while "dit", "lon_123", "con.cac" are rejected.
const TOKENS = new Set([
	// English
	"ass", "cum", "fag", "hoe", "tit", "tits", "sex", "anal", "anus", "nude", "nudes",
	"rape", "pedo", "dick", "cock", "kys", "kkk", "milf", "wank", "root", "system", "support",
	// Vietnamese (accent-free)
	"dit", "lon", "loz", "cac", "cak", "buoi", "deo", "dek", "dm", "dkm", "vl", "vcc",
	"cl", "cc", "dime", "cave", "ngu", "oc",
]);

// Returns the offending word, or null if the name is acceptable.
const findOffensiveWord = (name) => {
	const n = normalize(name);
	for (const t of n.split(/[^a-z]+/).filter(Boolean)) {
		if (TOKENS.has(t)) return t;
		if (TOKENS.has(squeeze(t))) return squeeze(t);
	}
	const joined = n.replace(/[^a-z]/g, "");
	for (const v of [joined, squeeze(joined)]) {
		for (const w of SUBSTRINGS) if (v.includes(w)) return w;
	}
	return null;
};

const isOffensiveUsername = (name) => findOffensiveWord(name) !== null;

// ---- Free text (e.g. order notes) ----
// Stricter about context than usernames: words are checked one by one and in
// windows of 2-4 adjacent words ("con cac", "dit me", "oc cho"), never across
// the whole sentence, so innocent combinations don't trigger. Common
// Vietnamese words that are only offensive in usernames are skipped:
// "2 lon coca", "cac mon", "oc luoc", "buoi toi", "deo", "ngu".
const TEXT_SAFE_TOKENS = new Set(["lon", "cac", "oc", "buoi", "deo", "ngu", "cave", "dime", "root", "system", "support"]);
const TEXT_SKIP_SUBSTRINGS = new Set(["admin", "moderator", "official"]);
const TEXT_ALLOW_WORDS = new Set(["shiitake", "shitake", "scunthorpe"]);

const findOffensiveText = (text) => {
	const words = normalize(text).split(/[^a-z]+/).filter((w) => w && !TEXT_ALLOW_WORDS.has(w));
	for (const w of words) {
		if (TEXT_SAFE_TOKENS.has(w)) continue;
		if (TOKENS.has(w)) return w;
		if (TOKENS.has(squeeze(w)) && !TEXT_SAFE_TOKENS.has(squeeze(w))) return squeeze(w);
	}
	for (let i = 0; i < words.length; i++) {
		for (let len = 1; len <= 4 && i + len <= words.length; len++) {
			const slice = words.slice(i, i + len);
			const joined = slice.join("");
			for (const v of [joined, squeeze(joined)]) {
				for (const sw of SUBSTRINGS) {
					if (!TEXT_SKIP_SUBSTRINGS.has(sw) && v.includes(sw)) return sw;
				}
			}
		}
	}
	return null;
};

const isOffensiveText = (text) => findOffensiveText(text) !== null;

export { normalize, findOffensiveWord, isOffensiveUsername, findOffensiveText, isOffensiveText };
